import React, {useState, useEffect} from 'react';
import {makeRequest} from "../../utils/httpRequest";
import DatePicker from "react-datepicker";
import {
    calculateDaysDifference,
    formatDateToAmericanFormat,
    formatDateToString, formatLocalDate, parseLocalDate, removeTimeFromDate
} from "../../utils/utils";
import {getPaymentById} from "../../utils/requestUtils";
import Config from "../../core/config";
import {useRecoilState} from "recoil";
import {loadingAtom} from "../../atoms";
import Cookies from "js-cookie";
import {getAuthHeader} from "../../utils/auth";
import {useNavigate} from "react-router-dom";
import Loading from "../../core/Loading";
import {UserPermission} from "../../utils/utils";
import {usePermission} from "../../core/PermissionContext";
import {saveAs} from 'file-saver';

const apiUrl = Config.apiUrl;

const PaymentList = () => {
    const { hasUserPermission, loadingPermissions } = usePermission();
    const [payments, setPayments] = useState([]);
    const [startDate, setStartDate] = useState(undefined);
    const initialEndDate = undefined;
    const [endDate, setEndDate] = useState(initialEndDate);
    const [status, setStatus] = useState(0);
    const [totalValue, setTotalValue] = useState(0);
    const [loading, setLoading] = useRecoilState(loadingAtom);
    const navigate = useNavigate();

    async function makeQuery() {
        try {
            setLoading(true);
            const newStartDate = startDate ? startDate : new Date(1900, 0, 1);
            const newEndDate = endDate ? endDate : new Date(2100, 0, 1);

            const url = `${apiUrl}/api/payments/queryByPaymentDate?startDate=${formatDateToAmericanFormat(newStartDate)}&endDate=${formatDateToAmericanFormat(newEndDate)}&status=${status}`;
            const response = await makeRequest(url, 'GET', {'Useruuid':Cookies.get('userUUID')});
            setPayments(response);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        makeQuery();
    }, []);

    useEffect(() => {
        setTotalValue(payments.reduce((acc, obj) => acc + obj.value, 0));
    }, [payments]);

    async function deletePayment(id) {
        try {
            setLoading(true);
            const url = apiUrl + '/api/payments/' + id;
            await makeRequest(url, 'DELETE', {'Useruuid':Cookies.get('userUUID')});
            await makeQuery();
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }

    const handleQuery = async (e) => {
        await makeQuery();
    };

    const handleStartDateChange = (date) => {
        setStartDate(date);
    };

    const handleEndDateChange = (date) => {
        setEndDate(date);
    };

    const generateExcelFile = async () => {
        try {
            const newStartDate = startDate ? startDate : new Date(1900, 0, 1);
            const newEndDate = endDate ? endDate : new Date(2100, 0, 1);
            const url = `${apiUrl}/api/payments/excelByPaymentDate?startDate=${formatDateToAmericanFormat(newStartDate)}&endDate=${formatDateToAmericanFormat(newEndDate)}&status=${status}`;

            const response = await fetch(url, {headers: {...getAuthHeader()}});
            if (response.ok) {
                const data = await response.blob();
                saveAs(data, `relatorio-pagamentos-${formatDateToAmericanFormat(newStartDate)}-${formatDateToAmericanFormat(newEndDate)}.xlsx`);
            } else {
                console.error('Erro ao gerar o arquivo Excel:', response.statusText);
            }
        } catch (error) {
            console.error('Erro ao fazer a solicitação para gerar o arquivo Excel:', error);
        }
    };

    const onPayClick = async (id) => {
        const payment = await getPaymentById(id);
        if (!window.confirm(`Deseja realmente ${!payment.paymentDate ? "Baixar" : "Estornar"}?`)) {
            return;
        }
        if (payment.paymentDate) {
            payment.paymentDate = undefined;
            payment.status = 0;
        } else {
            payment.paymentDate = formatLocalDate(new Date());
            payment.status = 1;
        }

        const url = apiUrl + '/api/payments';
        await makeRequest(url, 'PUT', {
            'Content-Type': 'application/json',
            'Useruuid':Cookies.get('userUUID')
        }, JSON.stringify(payment));

        await makeQuery();
    };

    function getBackgroundColor(payment) {
        const currentDate = removeTimeFromDate(new Date());
        const dueDate = parseLocalDate(payment.dueDate);
        const errorColor = 'var(--error-color)';
        const warningColor = 'var(--warning-color)';
        const normalColor = 'var(--normal-color)';
        if (payment.status === 0) {
            if (dueDate < currentDate)
                return errorColor;
            console.log('getBackgroundColor', dueDate, currentDate, calculateDaysDifference(dueDate, currentDate));
            if (calculateDaysDifference(dueDate, currentDate) === 0) {
                return warningColor;
            }
        }
        return normalColor;
    }

    if (loadingPermissions) {
        return <Loading></Loading>;
    }
    if (hasUserPermission(UserPermission.LIST_PAYMENT) === false) {
        navigate('/unauthorizedaccess');
        return null;
    }

    return (
        <>
            <div style={{display: "flex", alignItems: "center", gap: '10px'}}>
                <div className="header-label">Consulta de Pagamentos</div>
                {hasUserPermission(UserPermission.CREATE_PAYMENT) &&
                    <button className={"new-button"} onClick={() => navigate('/app/payment')}>Novo</button>
                }
            </div>

            <div
                style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', width: '100%'}}>
                <div style={{marginTop: '5px', alignItems: 'center'}}>

                    Data de Vencimento:
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <DatePicker
                            className="datepicker"
                            selected={startDate}
                            onChange={handleStartDateChange}
                            dateFormat="dd/MM/yyyy"
                            isClearable
                            autoComplete="off"
                        />

                        até
                        <DatePicker
                            className="datepicker"
                            selected={endDate}
                            onChange={handleEndDateChange}
                            dateFormat="dd/MM/yyyy"
                            isClearable
                            autoComplete="off"
                        />

                        <select value={status} onChange={(e) => setStatus(parseInt(e.target.value))}>
                            <option key={-1} value={-1}>Todos</option>
                            <option key={0} value={0}>Pendente</option>
                            <option key={1} value={1}>Pago</option>
                        </select>

                        <button onClick={handleQuery}>Consultar</button>
                    </div>
                </div>
                <div style={{marginTop: '5px', alignItems: 'center'}}>
                    <button className="excel-button" onClick={generateExcelFile}>Excel</button>
                </div>
            </div>

            <table style={{width: '1000px', height: '420px'}}>
                <tr>
                    <th>
                        Código
                    </th>
                    <th>
                        Cliente
                    </th>
                    <th>
                        Descrição
                    </th>
                    <th>
                        Lançamento
                    </th>
                    <th>
                        Vencimento
                    </th>
                    <th>
                        Pagamento
                    </th>
                    <th>
                        Valor
                    </th>
                    <th/>
                    <th/>
                    <th/>
                </tr>
                <tbody style={{overflowY: 'auto'}}>
                {payments.map((payment, index) => (
                    <tr key={index} style={{backgroundColor: getBackgroundColor(payment)}}>
                        <td>
                            <div>{payment.code}</div>
                        </td>
                        <td>
                            <div>{payment.supplier.name}</div>
                        </td>
                        <td>
                            <div>{payment.description}</div>
                        </td>
                        <td>
                            <div>{formatDateToString(new Date(payment.date))}</div>
                        </td>
                        <td>
                            <div>{formatDateToString(parseLocalDate(payment.dueDate))}</div>
                        </td>
                        <td>
                            <div>{payment.paymentDate ? formatDateToString(parseLocalDate(payment.paymentDate)) : "Pendente"}</div>
                        </td>
                        <td>
                            <div>R$ {payment.value?.toFixed(2)}</div>
                        </td>
                        <td>
                            <button onClick={async () => {
                                await onPayClick(payment.id);
                            }}>{payment.paymentDate ? "Estornar" : "Baixar"}</button>
                        </td>
                        <td>
                            {hasUserPermission(UserPermission.EDIT_PAYMENT) &&
                                <button className="edit-button" onClick={async () => {
                                    await navigate('/app/payment?id=' + payment.id);
                                }}>Editar</button>
                            }
                        </td>
                        <td>
                            {hasUserPermission(UserPermission.REMOVE_PAYMENT) &&
                                <button className="remove-button" onClick={async () => {
                                    // eslint-disable-next-line no-restricted-globals
                                    if (window.confirm('Deseja realmente excluir?')) {
                                        await deletePayment(payment.id);
                                    }
                                }}>Excluir</button>
                            }
                        </td>
                    </tr>
                ))}
                </tbody>
                {payments.length === 0 && (<tfoot><tr><td colSpan="10">{loading ? 'Carregando...' : 'Nenhum item encontrado.'}</td></tr></tfoot>)}
            </table>

            <div style={{marginTop: '10px'}}>
                <div className="total-label">Total: R$ {totalValue?.toFixed(2)}</div>
            </div>
        </>
    );
};

export default PaymentList;
